import { useEffect, useState, type CSSProperties } from "react";
import { usePostHog } from "posthog-js/react";
import { useTranslation } from "react-i18next";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { pushToDataLayer } from "@/lib/dataLayer";
import {
  APP_DOWNLOAD_URL,
  MOBILE_PROMPT_ATTRIBUTION,
  type AppDownloadPlatform,
  createAppDownloadClickId,
  getAppDownloadPlatform,
  getAppDownloadTrackingParams,
  getWebsiteStoreUrl,
  openMobileAppOrStore,
} from "@/lib/appDownloadLinks";

const CONTINUE_WEB_KEY = "solmi-app-prompt-continue-web-v2";
const MOBILE_APP_PROMPT_ENABLED = true;

function getPlatform(): AppDownloadPlatform {
  if (typeof navigator === "undefined") return "other";
  return getAppDownloadPlatform(navigator.userAgent);
}

function isSmallScreen(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 768;
}

function isMobileContext(platform: AppDownloadPlatform): boolean {
  if (platform === "ios" || platform === "android") return true;
  return isSmallScreen();
}

function hasContinuedOnWeb(): boolean {
  try {
    return localStorage.getItem(CONTINUE_WEB_KEY) === "true";
  } catch {
    return false;
  }
}

function setContinuedOnWeb() {
  try {
    localStorage.setItem(CONTINUE_WEB_KEY, "true");
  } catch {
    // Ignore storage failures; the prompt still closes for the current view.
  }
}

function getOpenTarget(platform: AppDownloadPlatform): string {
  return platform === "ios" || platform === "android"
    ? getWebsiteStoreUrl(platform, MOBILE_PROMPT_ATTRIBUTION)
    : APP_DOWNLOAD_URL;
}

function useMobileAppTarget() {
  const [target, setTarget] = useState<{
    mounted: boolean;
    isMobile: boolean;
    platform: AppDownloadPlatform;
  }>({
    mounted: false,
    isMobile: false,
    platform: "other",
  });

  useEffect(() => {
    const update = () => {
      const platform = getPlatform();
      setTarget({
        mounted: true,
        isMobile: isMobileContext(platform),
        platform,
      });
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return target;
}

type OpenAppButtonProps = {
  source?: string;
  className?: string;
  style?: CSSProperties;
};

export function MobileOpenAppButton({
  source = "mobile_open_app_button",
  className,
  style,
}: OpenAppButtonProps) {
  const { mounted, isMobile, platform } = useMobileAppTarget();
  const { t } = useTranslation();
  const posthog = usePostHog();

  if (!mounted || !isMobile) return null;

  const handleOpen = () => {
    const clickId = createAppDownloadClickId(source);
    const attribution = {
      ...MOBILE_PROMPT_ATTRIBUTION,
      content: source,
      clickId,
    };
    const trackingParams = getAppDownloadTrackingParams(attribution);
    const payload = { platform, source, ...trackingParams };

    posthog.capture("app_open_click", payload);
    pushToDataLayer({ event: "app_open_click", ...payload });
    window.gtag?.("event", "app_open_click", payload);
    openMobileAppOrStore(platform, attribution);
  };

  return (
    <button
      type="button"
      onClick={handleOpen}
      className={className}
      style={style}
    >
      {t("mobileAppPrompt.openAppButton", { defaultValue: "Open App" })}
    </button>
  );
}

export default function MobileAppPrompt() {
  const { mounted, isMobile, platform } = useMobileAppTarget();
  const { pathWithoutLang } = useLanguageRouter();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const posthog = usePostHog();

  const currentPath = pathWithoutLang.split(/[?#]/)[0] || "/";
  const isDownloadPage = currentPath === "/download";

  useEffect(() => {
    if (
      !MOBILE_APP_PROMPT_ENABLED ||
      !mounted ||
      !isMobile ||
      isDownloadPage ||
      hasContinuedOnWeb()
    ) {
      setVisible(false);
      return;
    }

    setVisible(true);
  }, [mounted, isMobile, isDownloadPage]);

  useEffect(() => {
    if (!visible || !posthog) return;
    const trackingParams = getAppDownloadTrackingParams(
      MOBILE_PROMPT_ATTRIBUTION,
    );
    posthog.capture("app_prompt_shown", { platform, ...trackingParams });
    pushToDataLayer({
      event: "app_prompt_shown",
      platform,
      ...trackingParams,
    });
  }, [visible, posthog, platform]);

  if (!MOBILE_APP_PROMPT_ENABLED || !visible) return null;

  const openTarget = getOpenTarget(platform);

  const handleOpen = () => {
    const clickId = createAppDownloadClickId("mobile_prompt");
    const attribution = {
      ...MOBILE_PROMPT_ATTRIBUTION,
      clickId,
    };
    const trackingParams = getAppDownloadTrackingParams(attribution);
    const payload = {
      platform,
      action: "open",
      ...trackingParams,
    };

    posthog.capture("app_prompt_click", payload);
    pushToDataLayer({ event: "app_prompt_click", ...payload });
    window.gtag?.("event", "app_prompt_click", payload);
    openMobileAppOrStore(platform, attribution);
  };

  const handleContinue = () => {
    const trackingParams = getAppDownloadTrackingParams(
      MOBILE_PROMPT_ATTRIBUTION,
    );
    const payload = {
      platform,
      action: "continue_web",
      ...trackingParams,
    };

    posthog.capture("app_prompt_click", payload);
    pushToDataLayer({
      event: "app_prompt_click",
      ...payload,
    });
    window.gtag?.("event", "app_prompt_click", payload);
    setContinuedOnWeb();
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[10000] md:hidden">
      <div className="mx-auto w-full max-w-lg rounded-t-[30px] bg-[#f7f3ee] px-6 pb-[calc(28px+env(safe-area-inset-bottom))] pt-8 text-[#111113] shadow-[0_-24px_80px_rgba(0,0,0,0.32)]">
        <h2 className="m-0 text-center text-[24px] font-semibold tracking-normal">
          {t("mobileAppPrompt.title", {
            defaultValue: "Solmi is better on the app",
          })}
        </h2>

        <div className="mt-7 grid gap-4">
          <div className="grid grid-cols-[1fr_minmax(150px,1.25fr)] items-center gap-4">
            <div className="text-[19px] font-semibold">
              {t("mobileAppPrompt.appLabel", { defaultValue: "Solmi App" })}
            </div>
            <a
              href={openTarget}
              onClick={(event) => {
                event.preventDefault();
                handleOpen();
              }}
              className="inline-flex h-[54px] items-center justify-center rounded-full bg-gradient-to-r from-violet-600 via-violet-500 to-emerald-500 px-5 text-[20px] font-semibold text-white shadow-[0_8px_24px_rgba(16,185,129,0.20)]"
            >
              {t("mobileAppPrompt.open", { defaultValue: "Open" })}
            </a>
          </div>

          <div className="grid grid-cols-[1fr_minmax(150px,1.25fr)] items-center gap-4">
            <div className="text-[19px] font-semibold">
              {t("mobileAppPrompt.browserLabel", {
                defaultValue: "Web Browser",
              })}
            </div>
            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex h-[54px] items-center justify-center rounded-full border border-[#c9c4bd] bg-transparent px-5 text-[20px] font-medium text-[#111113]"
            >
              {t("mobileAppPrompt.continue", { defaultValue: "Continue" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
